import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { QueryConfig, QueryFunction, QueryKey } from '../types';
import {
	buildDataQueryConfig,
	normalizeReactQueryKey,
	parseEnabledProp,
	useOnStateCallbacksReactQuery,
	useRefetchReactQueryOnDemand,
} from './utils';
import isFunction from 'lodash/isFunction';

export function useQueryV5<TResult, TError>(
	queryKey: QueryKey,
	queryFn: QueryFunction<TResult>,
	queryConfig: QueryConfig<TResult, TError> = {},
) {
	const normalizedQueryKey = useMemo(() => normalizeReactQueryKey(queryKey), [queryKey]);
	const normalizedEnabled = parseEnabledProp(queryConfig);
	const newReactQueryConfig = buildDataQueryConfig(queryConfig);
	const queryResult = useQuery({
		queryKey: normalizedQueryKey,
		queryFn: queryFn,
		...newReactQueryConfig,
	});
	const placeholderData = isFunction(newReactQueryConfig.placeholderData)
		? newReactQueryConfig.placeholderData()
		: newReactQueryConfig.placeholderData;
	const {
		data,
		error,
		isError,
		isFetched,
		isFetching,
		isLoading,
		isSuccess,
		refetch,
		status,
		isPending,
		fetchStatus,
	} = queryResult;

	useOnStateCallbacksReactQuery({
		queryConfig,
		queryResult,
	});

	useRefetchReactQueryOnDemand({
		queryKey: normalizedQueryKey,
		queryResult,
		enabled: normalizedEnabled,
	});

	return useMemo(() => {
		return {
			data: data || placeholderData,
			error,
			isError,
			isFetched,
			isFetching,
			isLoading,
			isPending,
			isSuccess,
			refetch,
			status,
			fetchStatus,
		};
	}, [
		placeholderData,
		data,
		error,
		isError,
		isFetched,
		isFetching,
		isLoading,
		isPending,
		isSuccess,
		refetch,
		status,
		fetchStatus,
	]);
}

/*
if (typeof queryConfig.staleTime === 'number') {
		newReactQueryConfig.staleTime = queryConfig.staleTime;
	}

	if (!isUndefined(placeholderDataRef.current)) {
		newReactQueryConfig.placeholderData = placeholderDataRef.current;
	}

	if (!isUndefined(initialDataRef.current)) {
		newReactQueryConfig.initialData = initialDataRef.current;
	}

	newReactQueryConfig.enabled = parsedEnabled;

	if (typeof queryConfig.cacheTime === 'number') {
		newReactQueryConfig.gcTime = queryConfig.cacheTime;
	}

		// useEffect(() => {
	// 	placeholderDataRef.current = queryConfig.placeholderData;
	// }, [queryConfig.placeholderData]);

	// useEffect(() => {
	// 	initialDataRef.current = queryConfig.initialData;
	// }, [queryConfig.initialData]);

	useEffect(() => {
		if (
			isFetched &&
			isSuccess &&
			isFetchedAfterMount &&
			!isPlaceholderData &&
			!isLoading &&
			!isFetching &&
			!isPending &&
			!isRefetching &&
			!isError &&
			!isUndefined(data) &&
			typeof onSuccessRef.current === 'function'
		) {
			onSuccessRef.current(data as TResult);
		}
	}, [
		data,
		isFetched,
		isSuccess,
		isPlaceholderData,
		isLoading,
		isFetching,
		isPending,
		isError,
		isRefetching,
		isFetchedAfterMount,
	]);

	useEffect(() => {
		if (
			isFetchedAfterMount &&
			!isLoading &&
			!isFetching &&
			!isPending &&
			!isRefetching &&
			error &&
			typeof onErrorRef.current === 'function'
		) {
			console.log(error);
			onErrorRef.current(error as TError);
		}
	}, [error, isFetchedAfterMount, isFetching, isLoading, isPending, isRefetching]);
*/
